import {
    xor as _xor,
    uniq as _uniq,
    isEmpty as _isEmpty,
    sortBy as _sortBy,
} from '@lodash';

import {
    ALL_SYSTEMS_SLUG,
    NO_FILTER,
} from './common';

import { Dispatch } from 'vuex';
import { IState } from '@store/index';
import i18n from '@app/localization/i18n';
import { IFilterState } from './types';
import LottoEvent from '@core/models/lotto/LottoEvent';
import numericalSystemComparator from '@core/utils/sortComparators/numericalSystemComparator';

function systemStringOf(event: {
    numbersDrawn: number;
    totalNumbers: number;
}) {
    return `${event.numbersDrawn}/${event.totalNumbers}`;
}

function systemFromSlug(slug:string): string {
    return slug.replace('-', '/');
}

function slugifySystem(system: string): string {
    return system.replace('/', '-');
}

export const mutations = {
};

export const getters = {
    /**
     * This getters return all available lotto systems. Example: ['7/39', '20/90']
     */
    systems: (state: IFilterState, getters: any, rootState: any, rootGetters: any): string[] =>
        // converts lotto event to string that is unique for lotto systems. example: '7/39'
        _uniq(getters._lottoEventsFilteredByDate.map(systemStringOf)),

    selectedSystemsSlugs(state: IFilterState, getters: any, rootState: any, rootGetters: any): string[] {
        // all events should be visible if ALL_SYSTEMS_SLUG is checked or no system is checked
        const { system } = rootGetters['navigation/route'].query;
        return system && system !== ALL_SYSTEMS_SLUG ? system.split(',') : [];
    },

    systemSelectors(state: IFilterState, getters: any, rootState: IState,
                    rootGetters: any) :{selected: boolean, name: string}[] {
        // This method is currently implemented suboptimally as it does not make much sense to
        // make any optimizations before it's decided how the systems should be sorted/displayed.
        const selectedSystems = getters.selectedSystemsSlugs.map(systemFromSlug);
        const selectedSet = new Set(); // new Set(iterable) does not work in IE9

        const selectedSelectors = selectedSystems.map((system: string) => {
            selectedSet.add(system); // we have to add items as we encounter them (IE9)
            return { selected: true, name: system };
        });

        const availableUnselected = getters.systems
            .filter((system: string) => !selectedSet.has(system))
            .map((system: string) => ({ selected: false, name: system }));

        const selectAll = {
            selected: getters.allSystemsSelected,
            name: `${i18n.t('all')}`,
        };

        const allSelectors = [...selectedSelectors, ...availableUnselected];
        allSelectors.sort(numericalSystemComparator);

        return [selectAll, ...allSelectors];
    },

    allSystemsSelected(state: IFilterState, getters: any, rootState: any, rootGetters: any): boolean {
        const { system } = rootGetters['navigation/route'].query;
        return system === ALL_SYSTEMS_SLUG;
    },

    /**
     * Returns a predicate used to filter available lotto events by systems.
     * The predicate depends on the currently selected systems.
     */
    systemFilter(state: IFilterState, getters: any) {
        const selectedSlugs = getters.selectedSystemsSlugs;

        // if there is no system preference, we do not filter available events
        if (getters.allSystemsSelected || _isEmpty(selectedSlugs)) {
            return NO_FILTER;
        }

        return (event: LottoEvent) => selectedSlugs.includes(slugifySystem(systemStringOf(event)));
    }
};

export const actions = {
    toggleSystem({ dispatch, getters }: { dispatch: Dispatch, getters: any }, selectedSystem: string) {
        const systems = _xor(
            getters.selectedSystemsSlugs.filter((system: string) => system !== ALL_SYSTEMS_SLUG),
            [slugifySystem(selectedSystem)]
        );
        dispatch('selectSystems', systems);
    },

    toggleAllSystem({ dispatch }: { dispatch: Dispatch }) {
        dispatch('selectSystems', [ALL_SYSTEMS_SLUG]);
    },

    selectSystems({ dispatch, rootGetters }: { dispatch: Dispatch, rootGetters: any },
                  selectedSystems: string[]) {
        const { timeFilter } = rootGetters['navigation/route'].params;
        const systemsSlug = selectedSystems.map((system: string) => slugifySystem(system)).join(',');
        /*
            This happens when the user checks the checkboxes on the systems tab. Replace is used to populate the query
            params.I don't think the user would intent to unselect a checkbox with the browser back button. I think the
            expected behaviour is that back changes the page, in this case to sport filter probably
        */
        dispatch(
            'navigation/replace',
            {
                params: { timeFilter },
                query: {
                    system: systemsSlug,
                },
            },
            { root: true });
    }
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/app/store/modules/ui/shared/lotto/filter/systemFilter.ts